import DataTable, { useDataTableHandle } from '../DataTable';
import { ExternalLinkAlt } from '@styled-icons/fa-solid';
import { postBusinessPartnerList } from '../apiCalls';
import { Container } from 'react-bootstrap';
import isoDateConverter from '../isoDateConverter';
import { useSecurity } from '../Security/security';
import { useState, useEffect, useContext } from 'react';
import { StoreContext } from '../Store';
import BusinessPartnerFilters from './BusinessPartnerFilters';
import ResetButton from '../Buttons/ResetButton';

function BusinessPartnersList({ neeNors, setNeeNor, setEditCreate, setNeeNors, origin = 'neenor' }) {
  const [paginationLoaded, setPaginationLoaded] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filterData, setFilterData] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const dth = useDataTableHandle();
  const store = useContext(StoreContext);
  const { validateRole } = useSecurity();

  const persistedFilters = store.businessPartnerFilters[0]?.filters || {};
  const hasFiltersApplied =
    !!Object.keys(persistedFilters).length ||
    (dth?.current?.sortStatus?.currentKey && Object.keys(dth.current.sortStatus || {}).length);

  const buildFilterPayload = (filters) => {
    const f = filters || persistedFilters;
    const str = (v) => (v != null && v !== '' ? String(v) : null);
    return {
      companyName: str(f.companyName),
      eori: str(f.eori),
      type: str(f.type),
      cabieStatus: str(f.cabieStatus),
      dbStatus: str(f.dbStatus),
      lastUpdatedFrom: str(f.lastModifiedFrom) || null,
      lastUpdatedTo: str(f.lastModifiedTo) || null,
    };
  };

  async function fetchData(newFilters, goToFirstPage = false, resetSort = false) {
    try {
      setIsLoading(true);
      const pagination = dth.current.pagination;
      const sortStatus = resetSort ? {} : dth.current.sortStatus;
      const filtersToSend = buildFilterPayload(newFilters);
      const hasFilters = Object.values(filtersToSend).some((v) => v != null);

      const payload = {
        skip: goToFirstPage ? 0 : pagination.pageNo * pagination.itemsPerPage,
        amount: pagination.itemsPerPage,
        sort_by: sortStatus?.currentKey ?? 'lastmodified',
        sort_order: sortStatus?.direction ?? 'desc',
        filter: filtersToSend,
        applyFilters: hasFilters ? 1 : 0,
      };

      const data = await postBusinessPartnerList(payload);
      dth.current.setServerDataLength(data.totalItems);
      setNeeNors(data.data);
      if (goToFirstPage) dth.current.resetPagination();
    } catch (err) {
      console.error('Error retrieving data');
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    if (hasFiltersApplied) {
      setFilterData({ ...persistedFilters });
      setShowFilters(true);
    }
  }, []);

  useEffect(() => {
    if (paginationLoaded) fetchData();
  }, [paginationLoaded]);

  async function updateData(setPaginationLength, pagination, sortStatus) {
    try {
      const filtersToSend = buildFilterPayload();
      const hasFilters = Object.values(filtersToSend).some((v) => v != null);
      const data = await postBusinessPartnerList({
        skip: pagination.pageNo * pagination.itemsPerPage,
        amount: pagination.itemsPerPage,
        sort_by: sortStatus?.currentKey ?? 'lastmodified',
        sort_order: sortStatus?.direction ?? 'desc',
        filter: filtersToSend,
        applyFilters: hasFilters ? 1 : 0,
      });
      setPaginationLength(data.totalItems);
      setNeeNors(data.data);
    } catch (err) {
      console.error('Error retrieving data');
    }
  }

  const handleReset = async () => {
    dth.current.resetPagination();
    dth.current.setSortStatus?.({});
    store.businessPartnerFilters[1]({});
    setFilterData({});
    await fetchData({}, true, true);
  };

  const handleSubmitFilters = async () => {
    store.businessPartnerFilters[1]({ filters: { ...filterData } });
    await fetchData(filterData, true);
  };

  const getNeenor = async (id) => {
    try {
      setNeeNor(id);
    } catch (err) {
      console.error(err);
    }
  };

  // only Ops Super, QA Super and CMT (standard or super) can create new business partner
  const isAllowedToCreateNew = validateRole({ role: 'SUPERUSER' }) || validateRole({ department: 'CMT' });

  return (
    <Container className="container-md">
      <section className="floating-box" style={{ maxWidth: '1600px' }}>
        <div className="d-flex justify-content-between align-items-center px-3 pt-4">
          <h1 className="page-heading">Business Partners</h1>
          <div className="d-flex gap-4 align-items-center flex-shrink-0">
            {hasFiltersApplied && <ResetButton onClick={handleReset} inline />}
            <button
              className="stepper-button"
              onClick={() => setShowFilters(!showFilters)}
              style={{ minWidth: 145, maxWidth: 145 }}
              disabled={hasFiltersApplied && showFilters}
            >
              {showFilters ? 'Hide' : 'Show'} Filters
            </button>
          </div>
        </div>

        <BusinessPartnerFilters
          visible={showFilters}
          values={filterData}
          onChange={setFilterData}
          onSubmit={handleSubmitFilters}
        />

        <div className="px-3 pt-4">
          <DataTable
            idKey="id"
            handle={dth}
            lazyLoading
            filterable={false}
            sortable
            data={neeNors}
            className="table-striped"
            advancedSearch
            showAdvnSearch={false}
            onUpdate={updateData}
            onPaginationLoaded={() => {
              setPaginationLoaded(true);
            }}
            persistGetter={store.bpPersist[0]}
            persistSetter={store.bpPersist[1]}
            isLoading={isLoading}
          >
            <DataTable.Head>
              {isAllowedToCreateNew && (
                <section className="d-flex justify-content-end datatable-button">
                  <button
                    type="button"
                    className="stepper-button yellow"
                    onClick={() => {
                      setEditCreate('create');
                      setNeeNor(0);
                    }}
                  >
                    New
                  </button>
                </section>
              )}
            </DataTable.Head>
            <DataTable.Pagination itemsPerPage={[10, 15, 50]} areaPadding={'0px'} />
            <DataTable.Headers />
            <DataTable.Column colKey="type" header="Type" searchOpts={{ type: 'text', sendAs: 'companyName' }}>
              {(item) => {
                return item?.type ?? '';
              }}
            </DataTable.Column>
            <DataTable.Column
              colKey="companyName"
              header="Company Name"
              searchOpts={{ type: 'text', sendAs: 'companyName' }}
              className="companyName"
            >
              {(item) => {
                return item.companyName?.slice(0, 30) + (item.companyName?.length > 30 ? '...' : '');
              }}
            </DataTable.Column>

            <DataTable.Column
              colKey="eori"
              header="EORI Number"
              searchOpts={{ type: 'text', sendAs: 'eori' }}
              className="eoriNumber"
            />
            <DataTable.Column
              colKey="cabiestatus"
              header="CABIE Status"
              searchOpts={{ type: 'text', sendAs: 'CABIE Status' }}
            />
            <DataTable.Column colKey="dbstatus" header="DB Status" searchOpts={{ type: 'text', sendAs: 'DB Status' }} />
            <DataTable.Column colKey="lastmodified" header="Last Updated">
              {(item) => {
                return isoDateConverter(item.lastmodified);
              }}
            </DataTable.Column>

            <DataTable.Column header="View" style={{ height: '1px', padding: 0 }} headerStyle={{ textAlign: 'center' }}>
              {(query) => (
                <button
                  className="external-table-link"
                  onClick={() => {
                    setEditCreate('edit');
                    getNeenor(query.id);
                  }}
                >
                  <ExternalLinkAlt width="1.1rem" height="1.1rem" color="#00857d" />
                </button>
              )}
            </DataTable.Column>
          </DataTable>
        </div>
      </section>
    </Container>
  );
}

export default BusinessPartnersList;
